import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useManualInterventions, queryKeys } from '../api/hooks';
import './ManualInterventionsPage.css';

const KIND_LABELS = {
  blocked: 'Blocked',
  open_override: 'Quoting + override',
  manual_book_suspend: 'Suspended',
  manual_shaping_leave_open: 'Shaping (leave open)',
  manual_price_over: 'Manual over price',
  manual_line: 'Manual line',
  manual_price_under: 'Manual under price',
  force_two_sided: 'Force two-sided',
  force_one_sided: 'Force one-sided',
  manual_logit_margin_scale: 'Logit margin scale',
  manual_prob_margin_over: 'Fixed over margin (Δp)',
  manual_prob_margin_under: 'Fixed under margin (Δp)',
  manual_base_line: 'Locked KF baseline',
  mute_competitor_signals: 'Competitor mute',
};

function formatGatedAt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatDec(x) {
  if (x == null || typeof x !== 'number' || Number.isNaN(x)) return '—';
  return Number(x).toFixed(3);
}

function formatOverUnder(over, under) {
  const o = formatDec(over);
  const u = formatDec(under);
  if (o === '—' && u === '—') return '—';
  return `${o} / ${u}`;
}

function formatEntryFees(row) {
  const n = row.n_entries ?? 0;
  const t = row.total_entry_fees;
  if (!n && (t == null || t === 0)) return '—';
  return `$${Number(t || 0).toFixed(0)} (${n} legs)`;
}

function formatPreGameHandleLimitUsd(usd) {
  if (usd == null || typeof usd !== 'number' || Number.isNaN(usd)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(usd);
}

/** Row is listed only for competitor mute (no prices, gate flags, baseline lock, etc.). */
function isMuteCompetitorOnlyRow(row) {
  const kinds = row.intervention_kinds;
  if (!Array.isArray(kinds) || kinds.length === 0) return false;
  return kinds.every((k) => k === 'mute_competitor_signals');
}

function ManualInterventionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useManualInterventions();
  const [excludeMuteCompetitorOnly, setExcludeMuteCompetitorOnly] = useState(true);
  const markets = useMemo(() => (Array.isArray(data?.markets) ? data.markets : []), [data]);
  const visibleMarkets = useMemo(() => {
    if (!excludeMuteCompetitorOnly) return markets;
    return markets.filter((row) => !isMuteCompetitorOnlyRow(row));
  }, [markets, excludeMuteCompetitorOnly]);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.manualInterventions });
  }, [queryClient]);

  const goToDashboard = useCallback(
    (marketKey) => {
      navigate(`/dashboard?market=${encodeURIComponent(marketKey)}`);
    },
    [navigate],
  );

  return (
    <div className="manual-interventions-page">
      <div className="page-header">
        <div>
          <h1>Manual interventions</h1>
          <p className="subtle" style={{ margin: '0.35rem 0 0' }}>
            Markets with a manual block, open-with-override, manual prices/line, forced one/two-sided
            quotes, manual logit margin scale, competitor mute, or a locked KF baseline (no automatic line selection).
            Alternate ladder lines are not published while any of these apply. Entry fees sum{' '}
            <code>market_entries</code> for this market key. KF decimals
            recomputed from stored <code>p_fair</code> / CI and market-type margination (last DB snapshot).
          </p>
        </div>
        <div className="manual-interventions-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <label className="subtle" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.875rem' }}>
            <input
              type="checkbox"
              checked={excludeMuteCompetitorOnly}
              onChange={(e) => setExcludeMuteCompetitorOnly(e.target.checked)}
            />
            Hide competitor-mute only
          </label>
          <button type="button" className="button" onClick={handleRefresh}>
            Refresh
          </button>
        </div>
      </div>

      <div className="card">
        {isLoading && markets.length === 0 ? (
          <div className="loading" style={{ padding: '1.5rem' }}>
            Loading…
          </div>
        ) : isError ? (
          <div style={{ padding: '1rem', color: '#f87171' }}>
            {error?.message || 'Failed to load manual interventions'}
          </div>
        ) : visibleMarkets.length === 0 ? (
          <div className="subtle" style={{ padding: '1.5rem' }}>
            {markets.length === 0
              ? 'No manual interventions in scope.'
              : 'No rows match the current filter (try showing competitor-mute-only markets).'}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Market</th>
                  <th>League</th>
                  <th>Sport</th>
                  <th>Entry fees</th>
                  <th>Pre-game limit</th>
                  <th>p (KF)</th>
                  <th>Published O / U</th>
                  <th>KF O / U</th>
                  <th>Intervention</th>
                  <th>Gate / reason</th>
                  <th>By</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {visibleMarkets.map((row) => (
                  <tr
                    key={row.market_key}
                    className="clickable-row"
                    onClick={() => goToDashboard(row.market_key)}
                    title="Open in dashboard"
                  >
                    <td style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.player_name || '—'}
                    </td>
                    <td
                      style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={row.market_key}
                    >
                      {row.market_id
                        ? `${String(row.market_id).replace(/_/g, ' ')}${row.line != null ? ` @ ${row.line}` : ''}`
                        : '—'}
                    </td>
                    <td>{row.league || '—'}</td>
                    <td>{row.sport || '—'}</td>
                    <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }} title="Total fees, entry leg count">
                      {formatEntryFees(row)}
                    </td>
                    <td
                      style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                      title="Pre-game handle cap from sport_market_limits"
                    >
                      {formatPreGameHandleLimitUsd(row.pre_game_handle_limit_usd)}
                    </td>
                    <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }} title="Stored KF fair (over)">
                      {row.p_fair != null && typeof row.p_fair === 'number' && !Number.isNaN(row.p_fair)
                        ? `${(row.p_fair * 100).toFixed(1)}%`
                        : '—'}
                    </td>
                    <td
                      style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                      title="marginated_price / marginated_price_under (if blocked: if_gated columns)"
                    >
                      {formatOverUnder(row.published_marginated_over, row.published_marginated_under)}
                    </td>
                    <td
                      style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                      title="From p_fair + CI using market-type margination (no manual logit)"
                    >
                      {formatOverUnder(row.kf_marginated_over, row.kf_marginated_under)}
                    </td>
                    <td style={{ maxWidth: '220px' }}>
                      {(row.intervention_kinds || []).map((k) => (
                        <span
                          key={k}
                          className={`kind-pill ${
                            [
                              'blocked',
                              'open_override',
                              'manual_book_suspend',
                              'manual_shaping_leave_open',
                              'mute_competitor_signals',
                            ].includes(k)
                              ? k
                              : ''
                          }`}
                        >
                          {KIND_LABELS[k] || k}
                        </span>
                      ))}
                    </td>
                    <td style={{ maxWidth: '200px', fontSize: '0.85rem', color: '#94a3b8' }}>
                      {row.gate_allowed === false ? (
                        <span style={{ color: '#f87171' }}>Blocked</span>
                      ) : (
                        <span style={{ color: '#4ade80' }}>Quoting</span>
                      )}
                      {row.gate_reason ? ` · ${row.gate_reason}` : ''}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{row.gated_by || '—'}</td>
                    <td style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      {formatGatedAt(row.gated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!isLoading && !isError && (
        <p className="subtle" style={{ marginTop: '0.75rem' }}>
          {excludeMuteCompetitorOnly && visibleMarkets.length !== markets.length
            ? `Showing ${visibleMarkets.length} of ${data?.count ?? markets.length} market(s).`
            : `${data?.count ?? markets.length} market(s).`}
          Scoped by API instance sport settings (same as other lists).
        </p>
      )}
    </div>
  );
}

export default ManualInterventionsPage;
